import Link from 'next/link';
import { useRouter } from 'next/router';
import { useRef, useCallback } from 'react';

/**
 * InstantLink — a next/link that navigates on pointerUP instead of waiting
 * for the browser's synthesized click (~80-120ms after touchend on mobile).
 *
 * Behavior:
 * - Records pointerdown position; on pointerup it fires ONLY if the finger
 *   moved <10px (a real tap, not a scroll start) and it's the primary,
 *   unmodified pointer/button.
 * - If an `onClick` handler is provided, the pointerup path invokes THAT
 *   handler (with the pointer event) — so existing nav handlers that
 *   preventDefault + router.push keep their exact semantics, just ~100ms
 *   sooner. Without `onClick`, it router.push/replace-es the href itself.
 * - The trailing synthesized click is swallowed so navigation never fires
 *   twice.
 * - Keyboard activation, middle/right clicks, and modifier-key clicks
 *   (cmd/ctrl new-tab) fall through to normal <Link>/onClick behavior.
 */
export default function InstantLink({
  href,
  as,
  replace = false,
  scroll = true,
  shallow = false,
  prefetch,
  onClick,
  children,
  ...rest
}) {
  const router = useRouter();
  const downRef = useRef(null);
  const firedRef = useRef(false);

  const handlePointerDown = useCallback((e) => {
    if (!e.isPrimary || e.button !== 0) {
      downRef.current = null;
      return;
    }
    downRef.current = { x: e.clientX, y: e.clientY };
  }, []);

  const handlePointerUp = useCallback((e) => {
    const down = downRef.current;
    downRef.current = null;
    if (!down || !e.isPrimary || e.button !== 0) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const dx = e.clientX - down.x;
    const dy = e.clientY - down.y;
    if (dx * dx + dy * dy > 100) return; // moved >10px: scroll/drag, not a tap
    firedRef.current = true;
    if (onClick) {
      onClick(e);
    } else {
      const method = replace ? router.replace : router.push;
      method.call(router, href, as, { scroll, shallow });
    }
  }, [onClick, router, href, as, replace, scroll, shallow]);

  const handlePointerCancel = useCallback(() => {
    downRef.current = null;
  }, []);

  const handleClick = useCallback((e) => {
    if (firedRef.current) {
      // Already navigated on pointerup — swallow the trailing click.
      firedRef.current = false;
      e.preventDefault();
      return;
    }
    // Keyboard / assistive tech / modifier clicks: normal behavior.
    if (onClick) onClick(e);
  }, [onClick]);

  return (
    <Link
      href={href}
      as={as}
      replace={replace}
      scroll={scroll}
      shallow={shallow}
      prefetch={prefetch}
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerCancel}
      onClick={handleClick}
      {...rest}
    >
      {children}
    </Link>
  );
}
